// DataSourceEntryModal — add or edit one row. Its fields are generated from the
// parent source's column schema, so one value is collected per column and sent
// as entryValues[{columnId, keyValue}].
//
// One component for both modes rather than a Create/Edit pair, because PATCH
// takes the create body unchanged — the form is identical and only the seed, the
// copy and the endpoint differ (the WorkflowBuilder idiom). Passing `entry`
// switches it to edit.
//
// Each value is checked against its column's dataType through `isCellValid` —
// the SAME predicate the CSV importer runs. A row typed here has to be exactly
// as valid as the same row imported from a file, and sharing the function rather
// than restating the rules is what keeps that true.
//
// That agreement extends to BLANKS: no field is required. The importer accepts an
// empty cell in any column, so the form does too — a value can be missing, and
// only a value that is PRESENT has to have the right shape. An empty column is
// submitted as `''`.

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { Button, Field, Input, Modal } from '@/components/common';
import type { DataSourceColumn } from '@/api/endpoints/datasourceApi';
import { columnTypeExample, columnTypeLabel } from '../shared/columnTypes';
import { isCellValid } from '../shared/parseDataSourceCsv';
import {
  useCreateDataSourceEntryMutation,
  useUpdateDataSourceEntryMutation,
  type DataSourceEntryListItem,
} from '@/api/endpoints/datasourceEntriesApi';

interface DataSourceEntryModalProps {
  open: boolean;
  onClose: () => void;
  dataSourceId: string;
  columns: DataSourceColumn[];
  /** Present → edit that row; absent → add a new one. */
  entry?: DataSourceEntryListItem;
}

const DataSourceEntryModal = ({
  open,
  onClose,
  dataSourceId,
  columns,
  entry,
}: DataSourceEntryModalProps) => {
  const { t } = useTranslation();
  const [createEntry, { isLoading: creating }] = useCreateDataSourceEntryMutation();
  const [updateEntry, { isLoading: updating }] = useUpdateDataSourceEntryMutation();
  // One value per column, keyed by columnId.
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);

  const editing = !!entry;
  const saving = creating || updating;

  // Re-seed each time the modal opens: the row's current values in edit mode,
  // empty in create. Discards leftovers from a previous open either way.
  useEffect(() => {
    if (open) {
      setDraft(entry ? { ...entry.values } : {});
      setSubmitted(false);
    }
  }, [open, entry]);

  const patch = (columnId: string, value: string) => setDraft((d) => ({ ...d, [columnId]: value }));

  // No field is required: a blank cell is a missing value, not a malformed one,
  // and the CSV importer accepts blanks in every column. The form has to agree,
  // or the same row would be legal imported and illegal typed.
  //
  // Test the TRIMMED value — that is what submit sends. isCellValid already
  // passes empty, so the guard below is only about SHAPE.
  const isWrongType = (column: DataSourceColumn) => {
    const value = (draft[column.columnId] ?? '').trim();
    return !!value && !isCellValid(column.dataType, value);
  };

  const errorFor = (column: DataSourceColumn): string | undefined =>
    submitted && isWrongType(column)
      ? t('datasource_entries_value_invalid', {
          type: columnTypeLabel(column.dataType, t),
          example: columnTypeExample(column.dataType, t),
        })
      : undefined;

  const anyWrongType = columns.some(isWrongType);

  const submit = async () => {
    // Validation runs BEFORE any backend call: mark the form as submitted so
    // errors show, and bail while anything is invalid. Type checking goes
    // through the SAME predicate the CSV importer uses, so a row typed here is
    // exactly as valid as the same row imported from a file.
    setSubmitted(true);
    if (anyWrongType) return;
    const body = {
      dataSource: dataSourceId,
      entryValues: columns.map((column) => ({
        columnId: column.columnId,
        keyValue: (draft[column.columnId] ?? '').trim(),
      })),
    };
    try {
      if (entry) await updateEntry({ id: entry.id, body }).unwrap();
      else await createEntry(body).unwrap();
      toast.success(t(editing ? 'datasource_entries_updated' : 'datasource_entries_created'));
      onClose();
    } catch {
      toast.error(t('error_generic'));
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      icon={editing ? 'square-pen' : 'folder-plus'}
      title={t(editing ? 'datasource_entries_edit_title' : 'datasource_entries_create_title')}
      subtitle={t('datasource_entries_create_subtitle')}
      footer={
        <>
          <Button variant='secondary' onClick={onClose} disabled={saving}>
            {t('button_cancel')}
          </Button>
          <Button iconStart={editing ? 'check' : 'plus'} onClick={submit} loading={saving}>
            {t(editing ? 'datasource_entries_save' : 'datasource_entries_create')}
          </Button>
        </>
      }
    >
      <div className='datasource-create'>
        {columns.map((column) => (
          <Field
            key={column.columnId}
            label={`${column.columnName} · ${columnTypeLabel(column.dataType, t)}`}
            error={errorFor(column)}
          >
            <Input
              value={draft[column.columnId] ?? ''}
              // A typed column shows its expected shape up front; `text` accepts
              // anything, so there the column's own name is the better hint.
              placeholder={
                column.dataType === 'text'
                  ? column.columnName
                  : columnTypeExample(column.dataType, t)
              }
              error={!!errorFor(column)}
              onChange={(e) => patch(column.columnId, e.target.value)}
            />
          </Field>
        ))}
      </div>
    </Modal>
  );
};

export default DataSourceEntryModal;
